import type { LessonCategory } from "@/lib/training-api";

/**
 * Lesson parent catalogue (ADR-0015 Phase A) — language-invariant fields
 * only. Translatable prose (title, summary, content blocks) lives in
 * `translations/{lang}/lessons.json`. The public-API adapter in
 * `./index.ts` assembles parent + translations + (Phase B) entitlement
 * gating into the legacy `Lesson` / `LessonDetail` shape that
 * `training-api.ts` consumes.
 *
 * Field origins:
 * - `id`, `slug`: stable identifiers. `slug` defaults to `id` for the
 *   12 existing lessons; future authors can diverge them.
 * - `category`, `duration_min`, `emoji`, `colorFrom/To`, `tags`,
 *   `related_lesson_ids`: carried over from the pre-refactor schema.
 * - `state_scope` (new): optional USPS codes. Empty = universal — matches
 *   pre-refactor behavior where lessons had no state binding.
 * - `required_tiers` (new, ADR-0015 §"Subscription gating"): empty =
 *   available to all. Phase B wires the entitlement check at read time.
 * - `free_preview` (new, ADR-0015 §"Subscription gating"): `true` =
 *   visible without ANY entitlement (trial / teaser). All 12 existing
 *   lessons start as `free_preview: true` to preserve current behavior;
 *   the monetization sweep is a separate ticket.
 * - `country_diff_ids` (new): replaces the embedded `country_diffs[]`
 *   array. Resolves to records in `./country-diffs/catalogue.ts` plus
 *   per-locale prose in `./country-diffs/translations/{lang}/...`. Same
 *   1:M shape Directus will produce when ROADS-488 lands.
 */
export interface LessonParent {
  readonly id: string;
  readonly slug: string;
  readonly category: LessonCategory;
  readonly duration_min: number;
  readonly emoji: string;
  readonly colorFrom: string;
  readonly colorTo: string;
  readonly tags: readonly string[];
  readonly state_scope: readonly string[];
  readonly required_tiers: readonly string[];
  readonly free_preview: boolean;
  readonly related_lesson_ids: readonly string[];
  readonly country_diff_ids: readonly string[];
}

export const LESSON_PARENTS: readonly LessonParent[] = [
  {
    id: "traffic-signals",
    slug: "traffic-signals",
    category: "signs",
    duration_min: 8,
    emoji: "🚦",
    colorFrom: "from-green-500",
    colorTo: "to-emerald-700",
    tags: [
      "traffic lights",
      "intersections",
      "flashing red",
      "green arrow",
      "DMV essential",
    ],
    state_scope: [],
    required_tiers: [],
    free_preview: true,
    related_lesson_ids: [
      "road-signs",
      "intersections",
      "right-of-way-basics",
    ],
    country_diff_ids: [
      "traffic-signals__IN",
      "traffic-signals__GB",
      "traffic-signals__DE",
      "traffic-signals__MX",
      "traffic-signals__JP",
    ],
  },
  {
    id: "road-signs",
    slug: "road-signs",
    category: "signs",
    duration_min: 7,
    emoji: "🛑",
    colorFrom: "from-red-500",
    colorTo: "to-red-700",
    tags: [
      "stop sign",
      "yield",
      "warning signs",
      "sign shapes",
      "DMV essential",
    ],
    state_scope: [],
    required_tiers: [],
    free_preview: true,
    related_lesson_ids: [
      "traffic-signals",
      "speed-limits",
      "parking-rules",
    ],
    country_diff_ids: [
      "road-signs__IN",
      "road-signs__GB",
      "road-signs__DE",
    ],
  },
  {
    id: "speed-limits",
    slug: "speed-limits",
    category: "speed",
    duration_min: 6,
    emoji: "🏎️",
    colorFrom: "from-blue-500",
    colorTo: "to-blue-700",
    tags: [
      "speed limits",
      "mph",
      "school zones",
      "highway",
      "residential",
    ],
    state_scope: [],
    required_tiers: [],
    free_preview: true,
    related_lesson_ids: [
      "school-zones",
      "highway-driving",
      "adverse-weather",
    ],
    country_diff_ids: [
      "speed-limits__IN",
      "speed-limits__DE",
      "speed-limits__GB",
      "speed-limits__MX",
    ],
  },
  {
    id: "school-zones",
    slug: "school-zones",
    category: "speed",
    duration_min: 5,
    emoji: "🏫",
    colorFrom: "from-yellow-400",
    colorTo: "to-amber-600",
    tags: [
      "school zone",
      "work zone",
      "doubled fines",
      "flashing lights",
      "children",
    ],
    state_scope: [],
    required_tiers: [],
    free_preview: true,
    related_lesson_ids: [
      "speed-limits",
      "pedestrian-crosswalks",
      "road-signs",
    ],
    country_diff_ids: [
      "school-zones__IN",
      "school-zones__GB",
    ],
  },
  {
    id: "intersections",
    slug: "intersections",
    category: "right-of-way",
    duration_min: 9,
    emoji: "🔀",
    colorFrom: "from-violet-500",
    colorTo: "to-purple-700",
    tags: [
      "right of way",
      "intersections",
      "yield",
      "T-intersection",
      "uncontrolled",
    ],
    state_scope: [],
    required_tiers: [],
    free_preview: true,
    related_lesson_ids: [
      "four-way-stop",
      "traffic-signals",
      "pedestrian-crosswalks",
    ],
    country_diff_ids: [
      "intersections__IN",
      "intersections__GB",
      "intersections__DE",
    ],
  },
  {
    id: "four-way-stop",
    slug: "four-way-stop",
    category: "right-of-way",
    duration_min: 5,
    emoji: "✋",
    colorFrom: "from-rose-500",
    colorTo: "to-red-700",
    tags: [
      "4-way stop",
      "all-way stop",
      "stop sign",
      "intersection",
      "right of way",
    ],
    state_scope: [],
    required_tiers: [],
    free_preview: true,
    related_lesson_ids: [
      "intersections",
      "road-signs",
      "traffic-signals",
    ],
    country_diff_ids: [
      "four-way-stop__IN",
      "four-way-stop__GB",
      "four-way-stop__MX",
    ],
  },
  {
    id: "right-side-driving",
    slug: "right-side-driving",
    category: "road-rules",
    duration_min: 8,
    emoji: "🛣️",
    colorFrom: "from-slate-600",
    colorTo: "to-slate-800",
    tags: [
      "right-hand traffic",
      "lane position",
      "left-hand countries",
      "adjusting",
      "UK India Australia",
    ],
    state_scope: [],
    required_tiers: [],
    free_preview: true,
    related_lesson_ids: [
      "intersections",
      "highway-driving",
      "lane-changes",
    ],
    country_diff_ids: [
      "right-side-driving__GB",
      "right-side-driving__IN",
      "right-side-driving__AU",
      "right-side-driving__JP",
    ],
  },
  {
    id: "highway-driving",
    slug: "highway-driving",
    category: "road-rules",
    duration_min: 10,
    emoji: "🌉",
    colorFrom: "from-sky-500",
    colorTo: "to-blue-700",
    tags: [
      "highway",
      "freeway",
      "merging",
      "following distance",
      "lane changes",
      "passing",
    ],
    state_scope: [],
    required_tiers: [],
    free_preview: true,
    related_lesson_ids: [
      "speed-limits",
      "lane-changes",
      "adverse-weather",
    ],
    country_diff_ids: [
      "highway-driving__IN",
      "highway-driving__DE",
      "highway-driving__GB",
    ],
  },
  {
    id: "dui-bac",
    slug: "dui-bac",
    category: "alcohol-drugs",
    duration_min: 7,
    emoji: "🚫🍺",
    colorFrom: "from-red-700",
    colorTo: "to-red-900",
    tags: [
      "DUI",
      "DWI",
      "BAC",
      "blood alcohol",
      "zero tolerance",
      "drugs",
    ],
    state_scope: [],
    required_tiers: [],
    free_preview: true,
    related_lesson_ids: [
      "speed-limits",
      "road-signs",
      "adverse-weather",
    ],
    country_diff_ids: [
      "dui-bac__IN",
      "dui-bac__DE",
      "dui-bac__GB",
      "dui-bac__JP",
    ],
  },
  {
    id: "pedestrian-crosswalks",
    slug: "pedestrian-crosswalks",
    category: "pedestrians",
    duration_min: 6,
    emoji: "🚶",
    colorFrom: "from-teal-500",
    colorTo: "to-teal-700",
    tags: [
      "pedestrians",
      "crosswalks",
      "right of way",
      "school crossing",
      "jaywalking",
    ],
    state_scope: [],
    required_tiers: [],
    free_preview: true,
    related_lesson_ids: [
      "traffic-signals",
      "school-zones",
      "intersections",
    ],
    country_diff_ids: [
      "pedestrian-crosswalks__IN",
      "pedestrian-crosswalks__GB",
      "pedestrian-crosswalks__MX",
    ],
  },
  {
    id: "parking-rules",
    slug: "parking-rules",
    category: "parking",
    duration_min: 7,
    emoji: "🅿️",
    colorFrom: "from-indigo-500",
    colorTo: "to-indigo-700",
    tags: [
      "parking",
      "fire hydrant",
      "curb colors",
      "no parking",
      "handicapped",
    ],
    state_scope: [],
    required_tiers: [],
    free_preview: true,
    related_lesson_ids: [
      "road-signs",
      "pedestrian-crosswalks",
      "traffic-signals",
    ],
    country_diff_ids: [
      "parking-rules__IN",
      "parking-rules__GB",
      "parking-rules__DE",
    ],
  },
  {
    id: "adverse-weather",
    slug: "adverse-weather",
    category: "weather",
    duration_min: 9,
    emoji: "🌧️",
    colorFrom: "from-slate-500",
    colorTo: "to-slate-700",
    tags: [
      "rain",
      "snow",
      "ice",
      "fog",
      "hydroplaning",
      "skid control",
      "visibility",
    ],
    state_scope: [],
    required_tiers: [],
    free_preview: true,
    related_lesson_ids: [
      "speed-limits",
      "highway-driving",
      "dui-bac",
    ],
    country_diff_ids: [
      "adverse-weather__IN",
      "adverse-weather__GB",
      "adverse-weather__MX",
      "adverse-weather__DE",
    ],
  },
] as const;
